import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../../../utils/axiosInstance";

const HEADERS = [
  "Code",
  "Type",
  "Company Name",
  "Primary User",
  "Country",
  "Email",
  "Assignable",
  "Status",
  "Created At",
];

const TYPE_STYLES = {
  Individual: "border-l-2 border-sky-400",
  Company: "border-l-2 border-amber-400",
};

const TYPE_BADGE = {
  Individual: { label: "Individual", cls: "bg-sky-50 text-sky-700" },
  Company: { label: "Company", cls: "bg-amber-50 text-amber-700" },
};

const renderTypeBadge = (type) => {
  const badge = TYPE_BADGE[type];
  if (!badge) return <span className="text-gray-500">—</span>;
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.cls}`}>
      {badge.label}
    </span>
  );
};

const ClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await api.get("/clients", { withCredentials: true });
        setClients(response.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch clients");
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate("/clients/create-client")}
            className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
          >
            Add Client
          </button>
          <button className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded">
            Import from Excel
          </button>
          <button className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded">
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-blue-600" />
        </div>
      )}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{clients.length}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No clients found.
                      </td>
                    </tr>
                  ) : (
                    clients.map((client, index) => {
                      const primaryUser = client.primary_user || {};
                      const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                      const typeStyle = TYPE_STYLES[client.type] ?? "";
                      return (
                        <tr
                          key={client.id}
                          className={`${rowBg} ${typeStyle} hover:bg-blue-50 transition-colors`}
                        >
                          
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/clients/${client.id}`}
                              className="text-blue-600 hover:text-blue-800 underline font-medium"
                            >
                              {`CLT-${String(client.id).padStart(3, "0")}`}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {renderTypeBadge(client.type)}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {client.company_name || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {`${primaryUser.first_name || ""} ${primaryUser.last_name || ""}`.trim() || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {client.country || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {client.auth?.email ? (
                              <a
                                href={`mailto:${client.auth.email}`}
                                className="text-blue-600 hover:underline"
                              >
                                {client.auth.email}
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {client.assignable ? "Yes" : "No"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <span
                              className={`px-2 py-0.5 inline-block rounded-full text-[10px] font-semibold ${
                                client.can_login
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-200 text-gray-700"
                              }`}
                            >
                              {client.can_login ? "Enabled" : "Disabled"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">
                            {client.createdAt
                              ? new Date(client.createdAt).toLocaleDateString()
                              : "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientsPage;
